import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { i18n } from '@/i18n';
import {
  normalizeStoredLocale,
  toEmbeddedShellLanguage,
} from '@/i18n/languageCodes';
import { embeddedCommunicationService } from '@/services/embeddedCommunicationService';
import { userProfileService } from '@/services/userProfileService';
import { resolveDeploymentEnvironment } from '@/shell/runtimeEnvironment';
import {
  AppShellContext,
  type AppShellContextValue,
  type AppShellEnvironment,
  type AppShellLanguage,
  type AppShellResolvedTheme,
  type ThemeStudioPreferences,
  type AppShellTheme,
  type AppShellUserProfile,
} from '@/shell/AppShellContext';
import type { AuthUser } from '@/types/auth';
import { defaultThemeStudioPreferences } from '@/theme/themeStudioConfig';

const languageStorageKey = 'entity_portal_language';
const themeStorageKey = 'entity_portal_theme';
const environmentStorageKey = 'entity_portal_environment';
const themeStudioStorageKey = 'entity_portal_theme_studio_preferences';
const defaultShellPermissions = [
  'VIEW_PROFILE',
  'VIEW_SHORTCUTS',
  'CHANGE_LANGUAGE',
  'CHANGE_THEME',
] as const;

function readStoredValue<T extends string>(key: string, fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);

    if (!value) {
      return fallback;
    }

    return value as T;
  } catch {
    return fallback;
  }
}

function writeStoredValue(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures.
  }
}

function readStoredThemeStudioPreferences(): ThemeStudioPreferences {
  try {
    const value = window.localStorage.getItem(themeStudioStorageKey);

    if (!value) {
      return defaultThemeStudioPreferences;
    }

    const parsed = JSON.parse(value) as Partial<ThemeStudioPreferences>;
    return {
      ...defaultThemeStudioPreferences,
      ...parsed,
      recentColors: parsed.recentColors?.length
        ? parsed.recentColors
        : defaultThemeStudioPreferences.recentColors,
    };
  } catch {
    return defaultThemeStudioPreferences;
  }
}

function writeStoredThemeStudioPreferences(
  preferences: ThemeStudioPreferences,
): void {
  try {
    window.localStorage.setItem(
      themeStudioStorageKey,
      JSON.stringify(preferences),
    );
  } catch {
    // Ignore storage failures.
  }
}

function resolveTheme(theme: AppShellTheme): AppShellResolvedTheme {
  if (theme !== 'system') {
    return theme;
  }

  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function areBreadcrumbsEqual(
  left: Array<{ label: string; href?: string }>,
  right: Array<{ label: string; href?: string }>,
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((item, index) => {
    const next = right[index];
    return item.label === next.label && item.href === next.href;
  });
}

function mergeAuthenticatedUser(
  current: AppShellUserProfile | null,
  authUser: AuthUser,
  deploymentEnvironment: AppShellEnvironment,
): AppShellUserProfile {
  const currentPermissions = current?.permissions.length
    ? current.permissions
    : [...defaultShellPermissions];
  const currentRoles = current?.roles ?? [];

  return {
    id: authUser.id,
    displayName: authUser.displayName,
    email: authUser.email,
    avatar: authUser.photoUrl,
    roles: currentRoles,
    permissions: Array.from(new Set(currentPermissions)),
    preferredLanguage: current?.preferredLanguage ?? 'en-US',
    theme: current?.theme ?? 'system',
    environment: current?.environment ?? deploymentEnvironment,
    status: 'online',
    lastLogin: current?.lastLogin,
  };
}

interface AppShellProviderProps {
  children: ReactNode;
}

export function AppShellProvider({ children }: AppShellProviderProps) {
  const deploymentEnvironment = resolveDeploymentEnvironment();
  const [authenticatedUser, setAuthenticatedUserState] =
    useState<AuthUser | null>(null);
  const [userProfile, setUserProfile] = useState<AppShellUserProfile | null>(
    null,
  );
  const [isUserProfileLoading, setIsUserProfileLoading] = useState(false);
  const [userProfileError, setUserProfileError] = useState<string | null>(null);
  const [language, setLanguageState] = useState<AppShellLanguage>(() =>
    normalizeStoredLocale(readStoredValue(languageStorageKey, 'en-US')),
  );
  const [theme, setThemeState] = useState<AppShellTheme>(() =>
    readStoredValue<AppShellTheme>(themeStorageKey, 'system'),
  );
  const [themeStudioPreferences, setThemeStudioPreferencesState] =
    useState<ThemeStudioPreferences>(() => readStoredThemeStudioPreferences());
  const [environment, setEnvironmentState] = useState<AppShellEnvironment>(
    deploymentEnvironment,
  );
  const [currentProductId, setCurrentProductIdState] = useState<string | null>(
    null,
  );
  const [currentEntityId, setCurrentEntityIdState] = useState<string | null>(
    null,
  );
  const [breadcrumbs, setBreadcrumbsState] = useState<
    Array<{ label: string; href?: string }>
  >([]);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const resolvedTheme = useMemo(() => resolveTheme(theme), [theme]);

  const loadUserProfile = useCallback(
    async (authUser?: AuthUser | null) => {
      setIsUserProfileLoading(true);
      setUserProfileError(null);

      try {
        const profile = await userProfileService.getMe(
          authUser ?? authenticatedUser,
        );
        const permissions = profile?.permissions.length
          ? profile.permissions
          : await userProfileService.getPermissions();

        if (!profile) {
          setUserProfile((current) => {
            if (!current) {
              return null;
            }

            return {
              ...current,
              permissions: permissions.length
                ? permissions
                : current.permissions,
            };
          });

          setIsUserProfileLoading(false);
          return;
        }

        setUserProfile({
          ...profile,
          permissions: permissions.length ? permissions : profile.permissions,
        });
      } catch {
        setUserProfileError('profileLoadFailed');
      } finally {
        setIsUserProfileLoading(false);
      }
    },
    [authenticatedUser],
  );

  useEffect(() => {
    const preferences = userProfileService.readPreferences();

    if (preferences) {
      setLanguageState(normalizeStoredLocale(preferences.preferredLanguage));
      setThemeState(preferences.theme);
      setThemeStudioPreferencesState((current) => ({
        ...current,
        mode: preferences.theme,
      }));
    }
  }, []);

  useEffect(() => {
    writeStoredValue(languageStorageKey, language);
    void i18n.changeLanguage(language);
    document.documentElement.lang = language;
    embeddedCommunicationService.broadcast({
      type: 'language',
      payload: {
        language: toEmbeddedShellLanguage(language),
      },
    });
  }, [language]);

  useEffect(() => {
    writeStoredValue(themeStorageKey, theme);
  }, [theme]);

  useEffect(() => {
    writeStoredThemeStudioPreferences(themeStudioPreferences);
  }, [themeStudioPreferences]);

  useEffect(() => {
    writeStoredValue(environmentStorageKey, environment);
  }, [environment]);

  const setLanguage = useCallback(async (nextLanguage: AppShellLanguage) => {
    setLanguageState(nextLanguage);
    setUserProfile((current) =>
      current
        ? {
            ...current,
            preferredLanguage: nextLanguage,
          }
        : current,
    );
    await userProfileService.updateLanguage(nextLanguage);
  }, []);

  const setTheme = useCallback(async (nextTheme: AppShellTheme) => {
    setThemeState(nextTheme);
    setThemeStudioPreferencesState((current) => ({
      ...current,
      mode: nextTheme,
    }));
    setUserProfile((current) =>
      current
        ? {
            ...current,
            theme: nextTheme,
          }
        : current,
    );
    await userProfileService.updateTheme(nextTheme);
  }, []);

  const setThemeStudioPreferences = useCallback(
    (updates: Partial<ThemeStudioPreferences>) => {
      setThemeStudioPreferencesState((current) => {
        const next = {
          ...current,
          ...updates,
        };

        if (updates.accentColor) {
          next.recentColors = Array.from(
            new Set([updates.accentColor, ...current.recentColors]),
          ).slice(0, 8);
        }

        return next;
      });

      if (updates.mode) {
        setThemeState(updates.mode);
      }
    },
    [],
  );

  const replaceThemeStudioPreferences = useCallback(
    (preferences: ThemeStudioPreferences) => {
      setThemeStudioPreferencesState(preferences);
      setThemeState(preferences.mode);
    },
    [],
  );

  const saveThemeStudioPreferences = useCallback(async () => {
    await userProfileService.updatePreferences({
      preferredLanguage: language,
      theme: themeStudioPreferences.mode,
      environment,
    });
  }, [environment, language, themeStudioPreferences.mode]);

  const resetThemeStudioPreferences = useCallback(async () => {
    setThemeStudioPreferencesState(defaultThemeStudioPreferences);
    setThemeState(defaultThemeStudioPreferences.mode);
    await userProfileService.updatePreferences({
      preferredLanguage: language,
      theme: defaultThemeStudioPreferences.mode,
      environment,
    });
  }, [environment, language]);

  const setEnvironment = useCallback(
    async (nextEnvironment: AppShellEnvironment) => {
      setEnvironmentState(nextEnvironment);
      setUserProfile((current) =>
        current
          ? {
              ...current,
              environment: nextEnvironment,
            }
          : current,
      );
      await userProfileService.updatePreferences({
        preferredLanguage: language,
        theme,
        environment: nextEnvironment,
      });
    },
    [language, theme],
  );

  const onEnvironmentChanged = useCallback(
    async (nextEnvironment: AppShellEnvironment) => {
      await setEnvironment(nextEnvironment);
    },
    [setEnvironment],
  );

  const openProfileMenu = useCallback((anchorEl: HTMLElement) => {
    setProfileMenuAnchorEl(anchorEl);
  }, []);

  const closeProfileMenu = useCallback(() => {
    setProfileMenuAnchorEl(null);
  }, []);

  const setCurrentProductId = useCallback((productId: string | null) => {
    setCurrentProductIdState((current) =>
      current === productId ? current : productId,
    );
  }, []);

  const setCurrentEntityId = useCallback((entityId: string | null) => {
    setCurrentEntityIdState((current) =>
      current === entityId ? current : entityId,
    );
  }, []);

  const setBreadcrumbs = useCallback(
    (nextBreadcrumbs: Array<{ label: string; href?: string }>) => {
      setBreadcrumbsState((current) =>
        areBreadcrumbsEqual(current, nextBreadcrumbs)
          ? current
          : nextBreadcrumbs,
      );
    },
    [],
  );

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!userProfile?.permissions?.length) {
        return false;
      }

      if (userProfile.permissions.includes('*')) {
        return true;
      }

      return userProfile.permissions.includes(permission);
    },
    [userProfile?.permissions],
  );

  const setAuthenticatedUser = useCallback(
    (authUser: AuthUser | null) => {
      setAuthenticatedUserState(authUser);

      if (!authUser) {
        setUserProfile((current) =>
          current ? { ...current, status: 'offline' } : null,
        );
        return;
      }

      setUserProfile((current) =>
        mergeAuthenticatedUser(current, authUser, deploymentEnvironment),
      );
    },
    [deploymentEnvironment],
  );

  const value: AppShellContextValue = useMemo(
    () => ({
      userProfile,
      isUserProfileLoading,
      userProfileError,
      language,
      theme,
      resolvedTheme,
      themeStudioPreferences,
      environment,
      currentProductId,
      currentEntityId,
      breadcrumbs,
      profileMenuAnchorEl,
      isProfileMenuOpen: Boolean(profileMenuAnchorEl),
      setLanguage,
      setTheme,
      setThemeStudioPreferences,
      replaceThemeStudioPreferences,
      saveThemeStudioPreferences,
      resetThemeStudioPreferences,
      setEnvironment,
      onEnvironmentChanged,
      setCurrentProductId,
      setCurrentEntityId,
      setBreadcrumbs,
      openProfileMenu,
      closeProfileMenu,
      refreshUserProfile: loadUserProfile,
      retryLoadUserProfile: loadUserProfile,
      hasPermission,
      setAuthenticatedUser,
    }),
    [
      breadcrumbs,
      closeProfileMenu,
      currentEntityId,
      currentProductId,
      environment,
      hasPermission,
      isUserProfileLoading,
      language,
      loadUserProfile,
      onEnvironmentChanged,
      openProfileMenu,
      profileMenuAnchorEl,
      resolvedTheme,
      setAuthenticatedUser,
      setEnvironment,
      setThemeStudioPreferences,
      replaceThemeStudioPreferences,
      saveThemeStudioPreferences,
      resetThemeStudioPreferences,
      setBreadcrumbs,
      setCurrentEntityId,
      setCurrentProductId,
      setLanguage,
      setTheme,
      theme,
      themeStudioPreferences,
      userProfile,
      userProfileError,
    ],
  );

  return (
    <AppShellContext.Provider value={value}>
      {children}
    </AppShellContext.Provider>
  );
}
